import { Box, Typography, Grid, CircularProgress, alpha } from "@mui/material";

const LeftStats = ({
  overviewStats,
  showRightStats = false,
  hangingStyle = false,
  loading,
  onStatusLabelClick,
}) => {
  // Filter leftStats based on showRightStats prop
  const getFilteredLeftStats = () => {
    if (!overviewStats?.leftStats) return [];

    if (showRightStats) {
      const filtered = overviewStats.leftStats.filter((stat) =>
        [1, 2, 3, 8, 9].includes(stat.id)
      );

      // Merge in the "Active Team Members" / "Active Devices" cards so they
      // render in the same card group, without touching their labels.
      const rightStatsAsCards = (overviewStats.rightStats || []).map(
        (stat) => ({
          ...stat,
          id: `right-${stat.id}`,
          value: `${stat.value.active}/${stat.value.total}`,
          isInfoCard: true,
        })
      );

      return [...filtered, ...rightStatsAsCards];
    } else {
      // Show all leftStats items except the "Today" field (id 1) when showRightStats is false
      return overviewStats.leftStats.filter((stat) => stat.id !== 1);
    }
  };

  const filteredLeftStats = getFilteredLeftStats();

  // Function to map label to StatusTabs filter value
  const mapLabelToStatus = (label) => {
    switch (label?.toLowerCase()) {
      case "in queue":
        return "In Queue";
      case "in progress":
        return "In Progress";
      case "git":
        return "GIT";
      case "delay picking":
        return "Delay Picking";
      case "on hold":
        return "On Hold";
      case "completed":
        return "Completed";
      case "canceled":
        return "Canceled";
      case "priority":
        return "Priority";
      case "total":
      default:
        return "All";
    }
  };

  const handleStatClick = (label) => {
    console.log("Label clicked:", label);

    // Only trigger if the handler is provided (i.e., showRightStats is false)
    if (onStatusLabelClick && !showRightStats) {
      const statusValue = mapLabelToStatus(label);
      onStatusLabelClick(statusValue);
    }
  };

  return (
    <>
      <Grid item xs={12}>
        <Box
          sx={{
            backgroundColor: "white",
            borderRadius: "12px",
            boxShadow: "0 4px 12px rgba(0, 0, 0, 0.08)",
            height: "100%",
            border: "1px solid #F0F1F3",
          }}
        >
          {!showRightStats && (
            <Box
              sx={{
                padding: "16px 24px",
                paddingBottom: "8px",
                borderBottom: "1px solid #F0F1F3",
              }}
            >
              <Typography
                variant="h6"
                sx={{
                  fontFamily: "Lexend",
                  fontSize: "18px",
                  fontWeight: 600,
                  color: "#1D2939",
                }}
              >
                Orders Count Overview
              </Typography>
            </Box>
          )}

          {loading ? (
            <Box
              sx={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                height: showRightStats ? 120 : 130,
                width: "100%",
              }}
            >
              <CircularProgress size={32} />
            </Box>
          ) : (
            <Box
              sx={{
                display: showRightStats ? "flex" : "grid",
                flexWrap: showRightStats ? "wrap" : "unset",
                justifyContent: "unset",
                gridTemplateColumns: showRightStats
                  ? "none"
                  : `repeat(${Math.min(filteredLeftStats.length, 9)}, 1fr)`,
                gap: showRightStats ? "12px" : "16px",
                alignItems: "stretch",
                width: "100%",
                padding: "16px",
                gridAutoRows: showRightStats ? "unset" : "1fr",
                overflow: hangingStyle ? "visible" : "hidden",
              }}
            >
              {filteredLeftStats.map((stat, index) => {
                if (showRightStats) {
                  // Flat colored card layout (matches Delivery > Orders OrderCounts design)
                  const cardColor = stat.color || "#3B82F6";
                  const isTodayCard = stat.id === 1;
                  const isAllCard = stat.id === 2;
                  const cardBackground = isAllCard
                    ? alpha(cardColor, 0.2)
                    : alpha(cardColor, 0.1);
                  const cardHoverBackground = isAllCard
                    ? alpha(cardColor, 0.28)
                    : alpha(cardColor, 0.15);
                  const textColor = isAllCard ? "#424242" : cardColor;

                  // "Today" is a label-only tile (icon + date), no numeric value
                  // Info cards (Active Team Members/Devices) keep their label as-is
                  // Order-count cards get an " Orders" suffix (existing behavior)
                  const displayLabel = isTodayCard
                    ? stat.label
                    : stat.isInfoCard
                    ? stat.label
                    : `${stat.label} Orders`;

                  return (
                    <Box
                      key={`${stat.id}-${index}`}
                      sx={{
                        flex: "0 1 180px",
                        minWidth: "180px",
                        padding: "12px 16px",
                        borderRadius: "8px",
                        backgroundColor: cardBackground,
                        border: "1px solid transparent",
                        display: "flex",
                        flexDirection: "row",
                        alignItems: "center",
                        justifyContent: isTodayCard
                          ? "center"
                          : "space-between",
                        gap: 2,
                        transition: "all 0.2s ease-in-out",
                        "&:hover": {
                          backgroundColor: cardHoverBackground,
                          borderColor: cardColor,
                          transform: "translateY(-2px)",
                        },
                      }}
                    >
                      {isTodayCard && stat.icon && (
                        <Box sx={{ display: "flex", alignItems: "center" }}>
                          {stat.icon}
                        </Box>
                      )}
                      <Typography
                        sx={{
                          fontFamily: "Lexend",
                          fontSize: "14px",
                          fontWeight: 500,
                          color: textColor,
                          textAlign: "left",
                          flex: isTodayCard ? "unset" : 1,
                        }}
                      >
                        {displayLabel}
                      </Typography>

                      {!isTodayCard && (
                        <Typography
                          sx={{
                            fontFamily: "Lexend",
                            fontSize: "20px",
                            fontWeight: 700,
                            color: textColor,
                            minWidth: "40px",
                            textAlign: "right",
                          }}
                        >
                          {stat.value}
                        </Typography>
                      )}
                    </Box>
                  );
                }

                return (
                  <Box
                    key={`${stat.id}-${index}`}
                    onClick={() => handleStatClick(stat.label)}
                    sx={{
                      backgroundColor: "#F8F9FA",
                      border: "1px solid #E1E5E9",
                      borderRadius: "8px",
                      padding: "16px 12px",
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "center",
                      justifyContent: stat.id === 1 ? "center" : "flex-start",
                      minHeight: "100px",
                      position: "relative",
                      overflow: "hidden",
                      width: "auto",
                      marginBottom: hangingStyle ? "-32px" : "0",

                      cursor: "pointer",
                      transition: "all 0.3s cubic-bezier(0.4, 0.2, 1)",

                      boxShadow: "0 2px 4px rgba(0, 0, 0, 0.06)",

                      "&:hover": {
                        backgroundColor: "#FFFFFF",
                        borderColor: stat.color || "#3B82F6",
                        transform: "translateY(-2px)",
                        boxShadow: `0 8px 25px rgba(0, 0, 0, 0.12), 0 0 0 1px ${
                          stat.color || "#3B82F6"
                        }20`,

                        "&::before": {
                          content: '""',
                          position: "absolute",
                          top: 0,
                          left: 0,
                          right: 0,
                          bottom: 0,
                          background: `linear-gradient(135deg, ${
                            stat.color || "#3B82F6"
                          }08, transparent 50%)`,
                          borderRadius: "8px",
                          zIndex: 0,
                        },
                      },

                      "&:active": {
                        transform: "translateY(-1px)",
                        boxShadow: `0 4px 12px rgba(0, 0, 0, 0.15), 0 0 0 1px ${
                          stat.color || "#3B82F6"
                        }30`,
                      },

                      borderLeft: `3px solid ${stat.color || "#3B82F6"}20`,
                    }}
                  >
                    {/* Top section - Label with icon */}
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        minHeight: "28px",
                        gap: "6px",
                        position: "relative",
                        zIndex: 1,
                        marginBottom: 0,
                      }}
                    >
                      {stat.icon && (
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            opacity: 0.8,
                          }}
                        >
                          {stat.icon}
                        </Box>
                      )}
                      <Typography
                        variant="body2"
                        sx={{
                          fontFamily: "Lexend",
                          color: "#4B5563",
                          fontSize: "14px",
                          fontWeight: 500,
                          textAlign: "center",
                          letterSpacing: "0.01em",
                        }}
                      >
                        {stat.label}
                      </Typography>
                    </Box>

                    {/* Bottom section - Value */}
                    {stat.value !== "" && stat.id !== 1 && (
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          minHeight: "40px",
                          position: "relative",
                          zIndex: 1,
                        }}
                      >
                        <Typography
                          variant="body1"
                          sx={{
                            fontFamily: "Lexend",
                            fontSize: "28px",
                            fontWeight: 600,
                            color: stat.color || "#1F2937",
                            lineHeight: 1,
                            textAlign: "center",
                            textShadow: "0 1px 2px rgba(0, 0, 0, 0.05)",
                          }}
                        >
                          {stat.value}
                        </Typography>
                      </Box>
                    )}

                    {/* Decorative element */}
                    <Box
                      sx={{
                        position: "absolute",
                        top: "-10px",
                        right: "-10px",
                        width: "40px",
                        height: "40px",
                        borderRadius: "50%",
                        backgroundColor: `${stat.color || "#3B82F6"}10`,
                        opacity: 0.6,
                        zIndex: 0,
                      }}
                    />
                  </Box>
                );
              })}
            </Box>
          )}
        </Box>
      </Grid>
    </>
  );
};

export default LeftStats;
